import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUsers, selectUsersLoading, selectUsersError, selectUsersMessage } from '../../features/users/userSlice';
import { LoadingButton } from '../ui/LoadingSpinner';
import UserListSkeleton from './UserListSkeleton';
import { useListFilters } from '../../hooks/useListFilters';
import React from 'react';

// Memoized UserRow component to prevent unnecessary re-renders
const UserRow = React.memo(({
  user,
  actionLoading,
  onEdit,
  onDelete
}) => {
  return (
    <tr className={`hover:bg-silver-mist/50 transition-colors ${!user.is_active ? 'bg-silver-mist/30' : ''}`}>
      <td className="px-6 py-6 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-12">
            <div className="h-12 w-12 bg-fresh-leaf/10 border-2 border-fresh-leaf/20 flex items-center justify-center">
              <span className="text-lg font-bold text-forest-fern">
                {user.username.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-4">
            <div className="text-base font-semibold text-deep-jungle">{user.username}</div>
            <div className="text-sm text-steel">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-6 whitespace-nowrap">
        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-error'}`}>
          {user.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-6 py-6 whitespace-nowrap">
        <div className="flex flex-wrap gap-2">
          {user.userRoles && user.userRoles.map(ur => (
            <span key={ur.role.name} className="badge badge-info">
              {ur.role.name}
            </span>
          ))}
        </div>
      </td>
      <td className="px-6 py-6 whitespace-nowrap text-sm text-steel">
        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
      </td>
      <td className="px-6 py-6 whitespace-nowrap text-right">
        <div className="flex justify-end space-x-3">
          <LoadingButton
            onClick={() => onEdit(user)}
            isLoading={actionLoading[`edit-${user.id}`]}
            loadingText="..."
            variant="outline"
            size="small"
            className="px-3 py-1 text-sm font-medium text-fresh-leaf hover:text-forest-fern border border-fresh-leaf/30 hover:border-forest-fern hover:bg-fresh-leaf/5 transition-all focus-visible"
          >
            Edit
          </LoadingButton>
          <LoadingButton
            onClick={() => onDelete(user)}
            isLoading={actionLoading[`delete-${user.id}`]}
            loadingText="..."
            variant="outline"
            size="small"
            className="px-3 py-1 text-sm font-medium text-error hover:text-error border border-error/30 hover:border-error hover:bg-error/5 transition-all focus-visible"
          >
            Delete
          </LoadingButton>
        </div>
      </td>
    </tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.user.id === nextProps.user.id &&
    prevProps.user.username === nextProps.user.username &&
    prevProps.user.email === nextProps.user.email &&
    prevProps.user.is_active === nextProps.user.is_active &&
    prevProps.user.lastLogin === nextProps.user.lastLogin &&
    JSON.stringify(prevProps.user.userRoles) === JSON.stringify(nextProps.user.userRoles) &&
    prevProps.actionLoading[`edit-${prevProps.user.id}`] === nextProps.actionLoading[`edit-${nextProps.user.id}`] &&
    prevProps.actionLoading[`delete-${prevProps.user.id}`] === nextProps.actionLoading[`delete-${nextProps.user.id}`]
  );
});

UserRow.displayName = 'UserRow';

function UserList({ onCreateNew, onEdit, onDelete, initialLoad = false }) {
  // Use specific selectors instead of destructuring with robust type checking
  const usersFromState = useSelector(selectUsers);
  const users = Array.isArray(usersFromState) ? usersFromState : [];
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);

  const [actionLoading, setActionLoading] = useState({});
  
  // Configure and use the list filters hook
  const {
    filteredItems: filteredUsers,
    searchTerm,
    setSearchTerm,
    roleFilter,
    setRoleFilter,
    statusFilter,
    setStatusFilter,
    totalItems,
    filteredCount
  } = useListFilters(users, {
    searchFields: ['username', 'email'],
    roleFilter: {
      enabled: true,
      field: 'userRoles',
      roleNamePath: 'role.name'
    },
    statusFilter: {
      enabled: true,
      field: 'is_active',
      activeValue: true,
      inactiveValue: false
    }
  });
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle user actions with loading states
  const handleEdit = (user) => {
    setActionLoading(prev => ({ ...prev, [`edit-${user.id}`]: true }));
    // Simulate brief loading for better UX
    setTimeout(() => {
      setActionLoading(prev => ({ ...prev, [`edit-${user.id}`]: false }));
      onEdit(user);
    }, 300);
  };

  const handleDelete = (user) => {
    setActionLoading(prev => ({ ...prev, [`delete-${user.id}`]: true }));
    // Simulate brief loading for better UX
    setTimeout(() => {
      setActionLoading(prev => ({ ...prev, [`delete-${user.id}`]: false }));
      onDelete(user);
    }, 300);
  };
  
  // Get unique roles from all users
  const allRoles = [...new Set(
    users.flatMap(user => 
      user.userRoles ? user.userRoles.map(ur => ur.role.name) : []
    )
  )];

  // Show skeleton loading on initial load OR when users array is empty and loading
  if ((initialLoad && isLoading) || (isLoading && users.length === 0)) {
    return <UserListSkeleton />;
  }
  
  return (
    <div className="bg-crystal-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header Section */}
          <div className="border-b border-silver-mist pb-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="space-y-2">
                <h1 className="text-3xl font-display font-bold text-deep-jungle">User Management</h1>
                <p className="text-steel text-sm">Manage user accounts, roles, and permissions</p>
              </div>
              
              <LoadingButton
                onClick={onCreateNew}
                isLoading={isLoading && users.length === 0}
                loadingText="Loading..."
                variant="primary"
                className="btn-primary focus-visible inline-flex items-center whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Create New User
              </LoadingButton>
            </div>
          </div>
          
          {/* Search and Filters Section */}
          <div className="bg-silver-mist border border-woodland-moss/20 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
              {/* Search Input */}
              <div className="lg:col-span-6">
                <label className="block text-sm font-medium text-graphite mb-2">Search Users</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-steel" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search by username or email..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    disabled={isLoading && users.length === 0}
                    className="block w-full pl-10 pr-4 py-3 border border-woodland-moss/30 bg-crystal-white text-graphite placeholder-steel focus:border-fresh-leaf focus:ring-2 focus:ring-fresh-leaf/20 disabled:bg-silver-mist disabled:cursor-not-allowed transition-colors"
                  />
                </div>
              </div>
              
              {/* Role Filter */}
              <div className="lg:col-span-3">
                <label className="block text-sm font-medium text-graphite mb-2">Filter by Role</label>
                <select 
                  value={roleFilter} 
                  onChange={(e) => setRoleFilter(e.target.value)}
                  disabled={isLoading && users.length === 0}
                  className="block w-full px-4 py-3 border border-woodland-moss/30 bg-crystal-white text-graphite focus:border-fresh-leaf focus:ring-2 focus:ring-fresh-leaf/20 disabled:bg-silver-mist disabled:cursor-not-allowed transition-colors"
                >
                  <option value="">All Roles</option>
                  {allRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              
              {/* Status Filter */}
              <div className="lg:col-span-3">
                <label className="block text-sm font-medium text-graphite mb-2">Filter by Status</label>
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disabled={isLoading && users.length === 0}
                  className="block w-full px-4 py-3 border border-woodland-moss/30 bg-crystal-white text-graphite focus:border-fresh-leaf focus:ring-2 focus:ring-fresh-leaf/20 disabled:bg-silver-mist disabled:cursor-not-allowed transition-colors"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Results Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-1">
            <div className="text-sm text-steel">
              {totalItems > 0 ? (
                <>
                  Showing <span className="font-medium text-graphite">{filteredCount}</span> of <span className="font-medium text-graphite">{totalItems}</span> users
                  {isError && (
                    <span className="ml-2 text-error font-medium">
                      • Error: {message}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-steel">
                  {isError ? `Error loading users: ${message}` : 'No users available'}
                </span>
              )}
            </div>
            
            {/* Loading indicator for refresh */}
            {isLoading && users.length > 0 && (
              <div className="flex items-center space-x-2 text-sm text-steel">
                <div className="loading-spinner w-4 h-4"></div>
                <span>Refreshing...</span>
              </div>
            )}
          </div>
          
          {/* Users Table */}
          <div className="bg-crystal-white border border-silver-mist shadow-minimal overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-silver-mist">
                <thead className="bg-deep-jungle">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-crystal-white uppercase tracking-wider">
                      User Information
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-crystal-white uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-crystal-white uppercase tracking-wider">
                      Roles
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-crystal-white uppercase tracking-wider">
                      Last Login
                    </th>
                    <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-crystal-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-crystal-white divide-y divide-silver-mist">
                  {totalItems > 0 && filteredCount > 0 ? (
                    filteredUsers.map(user => {
                      // Console log removed for production
                       
                      
                      return (
                        <UserRow 
                          key={user.id}
                          user={user}
                          actionLoading={actionLoading}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center max-w-md mx-auto">
                          {isError ? (
                            <>
                              <div className="w-16 h-16 bg-error/10 border-2 border-error/20 flex items-center justify-center mb-4">
                                <svg className="h-8 w-8 text-error" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-deep-jungle mb-2">Failed to load users</h3>
                              <p className="text-steel text-center">{message}</p>
                            </>
                          ) : (
                            <>
                              <div className="w-16 h-16 bg-steel/10 border-2 border-steel/20 flex items-center justify-center mb-4">
                                <svg className="h-8 w-8 text-steel" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                                </svg>
                              </div>
                              <h3 className="text-lg font-semibold text-deep-jungle mb-2">
                                {totalItems === 0 ? 'No users found' : 'No users match your filters'}
                              </h3>
                              <p className="text-steel text-center">
                                {totalItems === 0 
                                  ? 'Get started by creating your first user' 
                                  : 'Try adjusting your search or filter criteria'}
                              </p>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserList;
